// Shared pane chrome for workspace-style pages: the section nav bar that
// tracks a scroll container, the pane header row, and the tab switcher. Used
// by the studio workspace (CalculatorWorkspace) and the CFO cost model page
// so both surfaces share one silhouette.

import { Fragment, useEffect, useRef, useState } from 'react'

const SECTION_NAV_TARGET_GAP = 24
const SECTION_NAV_INLINE_PADDING = 8

// The nav sits inside a max-width wrapper, so the scroll container is not
// necessarily its grandparent. Walk up to the nearest actually-scrollable
// ancestor instead of assuming a fixed depth.
function findScrollParent(node) {
  let el = node?.parentElement
  while (el) {
    const overflowY = window.getComputedStyle(el).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return el
    el = el.parentElement
  }
  return node?.parentElement || null
}

function getSectionTop(scrollRoot, section) {
  // Resolve against the live scroll root so outer pane chrome and the optional
  // Compare guide are reflected by the container's actual viewport position.
  const rootRect = scrollRoot.getBoundingClientRect()
  const sectionRect = section.getBoundingClientRect()
  return scrollRoot.scrollTop + sectionRect.top - rootRect.top
}

function getSectionNavOffset(navWrapper) {
  return navWrapper.getBoundingClientRect().height + SECTION_NAV_TARGET_GAP
}

// Renders as the pane's single header row: nav links left, `actions` docked
// right. Lives OUTSIDE the scroll container it tracks, so the scroll root is
// passed in via `scrollRootRef` (falling back to the nearest scroll parent).
export function SectionNav({ items, disabled = false, scrollRootRef = null, actions = null }) {
  const navRef = useRef(null)
  const clickScrollFrameRef = useRef(null)
  const layoutUpdateFrameRef = useRef(null)
  const isClickScrollingRef = useRef(false)
  const [hasMounted, setHasMounted] = useState(false)
  const [activeHref, setActiveHref] = useState(items[0]?.href)

  const handleSectionClick = (event, item) => {
    if (disabled) return

    const nav = navRef.current
    const navWrapper = nav?.parentElement
    const scrollRoot = scrollRootRef?.current ?? findScrollParent(nav)
    const section = scrollRoot?.querySelector(item.href)
    if (!nav || !navWrapper || !scrollRoot || !section) return

    event.preventDefault()
    // When the nav is a bar outside the scroller it doesn't overlay content,
    // so only the breathing gap applies – not the bar's own height.
    const navOffset = scrollRootRef ? SECTION_NAV_TARGET_GAP : getSectionNavOffset(navWrapper)
    const nextTop = getSectionTop(scrollRoot, section) - navOffset

    isClickScrollingRef.current = true
    setActiveHref(item.href)
    scrollRoot.scrollTo({ top: Math.max(0, nextTop), behavior: 'auto' })
    window.history.replaceState(null, '', item.href)

    if (clickScrollFrameRef.current) {
      cancelAnimationFrame(clickScrollFrameRef.current)
    }

    clickScrollFrameRef.current = requestAnimationFrame(() => {
      clickScrollFrameRef.current = requestAnimationFrame(() => {
        isClickScrollingRef.current = false
      })
    })
  }

  // A vertical wheel gesture over the nav scrolls it sideways – the row is
  // the only horizontally scrollable thing under the pointer there. Attached
  // natively (non-passive) because React's root-level wheel listener is
  // passive, so preventDefault from a React onWheel silently fails and the
  // browser fights the manual scroll. Horizontal-dominant gestures are left
  // to native overflow-x scrolling.
  useEffect(() => {
    const nav = navRef.current
    if (!nav) return

    const onWheel = (event) => {
      if (event.deltaY === 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
      const maxScrollLeft = nav.scrollWidth - nav.clientWidth
      const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, nav.scrollLeft + event.deltaY))
      if (nextScrollLeft === nav.scrollLeft) return

      event.preventDefault()
      nav.scrollLeft = nextScrollLeft
    }

    nav.addEventListener('wheel', onWheel, { passive: false })
    return () => nav.removeEventListener('wheel', onWheel)
  }, [])

  // Keep the active tab in view as it changes (clicks or content scrolling).
  // Measure via rects, not offsetLeft: the wrapper is a plain static flex row,
  // so offsetLeft would resolve against a distant positioned ancestor.
  useEffect(() => {
    const nav = navRef.current
    const activeTab = nav?.querySelector('a[aria-current="true"]')
    if (!nav || !activeTab) return

    const tabRect = activeTab.getBoundingClientRect()
    const navRect = nav.getBoundingClientRect()
    const tabLeft = tabRect.left - navRect.left + nav.scrollLeft
    const tabRight = tabLeft + tabRect.width
    const visibleLeft = nav.scrollLeft
    const visibleRight = visibleLeft + nav.clientWidth

    // Instant, not smooth: the same click also scrolls the content pane and
    // lazy charts re-layout, and the browser cancels a concurrent smooth
    // scroll – which left the nav stuck in place.
    if (tabLeft < visibleLeft) {
      nav.scrollLeft = Math.max(0, tabLeft - SECTION_NAV_INLINE_PADDING)
    } else if (tabRight > visibleRight) {
      nav.scrollLeft = tabRight - nav.clientWidth + SECTION_NAV_INLINE_PADDING
    }
  }, [activeHref])

  useEffect(() => {
    const nav = navRef.current
    const navWrapper = nav?.parentElement
    const scrollRoot = scrollRootRef?.current ?? findScrollParent(nav)
    if (!nav || !navWrapper || !scrollRoot) return

    const sections = items
      .map((item) => ({
        ...item,
        element: scrollRoot.querySelector(item.href),
      }))
      .filter((item) => item.element)

    if (sections.length === 0) return

    const updateActiveSection = () => {
      if (isClickScrollingRef.current) return
      // Skip computation when the pane is hidden (zero-height container yields bogus offsets)
      if (!scrollRoot.offsetHeight) return

      const navHeight = scrollRootRef ? 0 : navWrapper.getBoundingClientRect().height
      const viewportTop = scrollRoot.scrollTop + navHeight
      const activeLine = viewportTop + SECTION_NAV_TARGET_GAP
      const viewportBottom = scrollRoot.scrollTop + scrollRoot.clientHeight
      let nextActive = sections[0].href
      let activeSection = sections[0]

      for (const section of sections) {
        const sectionTop = getSectionTop(scrollRoot, section.element)
        if (sectionTop <= activeLine) {
          nextActive = section.href
          activeSection = section
        }
      }

      const activeSectionTop = getSectionTop(scrollRoot, activeSection.element)
      const visibleUpcomingSection = sections.find((section) => {
        const sectionTop = getSectionTop(scrollRoot, section.element)
        return (
          sectionTop > activeLine &&
          sectionTop < viewportBottom &&
          activeSectionTop < viewportTop
        )
      })

      if (visibleUpcomingSection) {
        nextActive = visibleUpcomingSection.href
      }

      setActiveHref(nextActive)
    }

    const scheduleActiveSectionUpdate = () => {
      if (layoutUpdateFrameRef.current) {
        cancelAnimationFrame(layoutUpdateFrameRef.current)
      }

      layoutUpdateFrameRef.current = requestAnimationFrame(() => {
        layoutUpdateFrameRef.current = null
        updateActiveSection()
      })
    }

    let resizeObserver = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleActiveSectionUpdate)
      resizeObserver.observe(scrollRoot)
      sections.forEach((section) => resizeObserver.observe(section.element))
    }

    updateActiveSection()
    scrollRoot.addEventListener('scroll', updateActiveSection, { passive: true })
    window.addEventListener('resize', updateActiveSection)

    // Enable transitions only after the first correct active section is painted.
    // Single rAF is enough since updateActiveSection() above already set the correct state.
    const mountFrame = requestAnimationFrame(() => {
      setHasMounted(true)
    })

    return () => {
      setHasMounted(false)
      scrollRoot.removeEventListener('scroll', updateActiveSection)
      window.removeEventListener('resize', updateActiveSection)
      resizeObserver?.disconnect()
      cancelAnimationFrame(mountFrame)
      if (clickScrollFrameRef.current) {
        cancelAnimationFrame(clickScrollFrameRef.current)
      }
      if (layoutUpdateFrameRef.current) {
        cancelAnimationFrame(layoutUpdateFrameRef.current)
      }
    }
    // `disabled` is a dep because the bar stays mounted while the pane content
    // swaps (blocked ↔ results); re-running re-finds the section anchors.
  }, [items, disabled, scrollRootRef])

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-border/60 bg-card/95 px-3 lg:px-4">
      <nav
        ref={navRef}
        className="section-nav-scroll flex min-w-0 flex-1 items-center gap-1 overflow-x-auto py-1.5"
        aria-label="Pane sections"
      >
        {items.map(item => (
          <Fragment key={item.href}>
            {item.groupLabel && (
              <span
                aria-hidden="true"
                className="ml-2 mr-0.5 flex shrink-0 items-center border-l border-border pl-2.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60"
              >
                {item.groupLabel}
              </span>
            )}
            <a
              href={disabled ? undefined : item.href}
              onClick={(event) => handleSectionClick(event, item)}
              aria-current={activeHref === item.href ? 'true' : undefined}
              aria-disabled={disabled || undefined}
              className={[
                'relative shrink-0 rounded px-3 py-2 text-sm font-medium hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50',
                hasMounted && 'transition-colors',
                activeHref === item.href
                  ? hasMounted
                    ? 'font-semibold text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-primary'
                    : 'text-foreground'
                  : 'text-muted-foreground',
                disabled && 'pointer-events-none cursor-not-allowed opacity-50',
              ].filter(Boolean).join(' ')}
            >
              {item.label}
            </a>
          </Fragment>
        ))}
      </nav>
      {actions && <div className="ml-auto flex shrink-0 items-center py-1.5 pl-3">{actions}</div>}
    </div>
  )
}


function scrollPaneFromWheel(event, pane) {
  if (!pane || event.deltaY === 0) return false

  const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pane.clientHeight : 1
  const maxScrollTop = pane.scrollHeight - pane.clientHeight
  const nextScrollTop = Math.min(maxScrollTop, Math.max(0, pane.scrollTop + event.deltaY * scale))
  if (nextScrollTop === pane.scrollTop) return false

  event.preventDefault()
  pane.scrollTop = nextScrollTop
  return true
}

export function PaneLabel({ label, switcher, scrollTargetRef, children }) {
  const rowRef = useRef(null)

  const handleWheel = (event) => {
    const row = rowRef.current
    // Radix renders dialogs/popovers in portals. Their React events still
    // bubble through this component tree even though the overlay is not inside
    // the pane header in the DOM. Only forward wheel input that physically
    // originated over the header; otherwise scrolling a portal would also
    // scroll the pane behind it.
    if (!row || !row.contains(event.target)) return

    const verticalGesture = !event.shiftKey && Math.abs(event.deltaY) >= Math.abs(event.deltaX)
    if (verticalGesture && scrollTargetRef?.current) {
      scrollPaneFromWheel(event, scrollTargetRef.current)
      return
    }

    const delta = event.deltaX || event.deltaY
    if (delta === 0) return
    const maxScrollLeft = row.scrollWidth - row.clientWidth
    const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, row.scrollLeft + delta))
    if (nextScrollLeft === row.scrollLeft) return

    event.preventDefault()
    row.scrollLeft = nextScrollLeft
  }

  // Single header row: tabs identify the pane (the name is screen-reader
  // only), the pane's one action docks right – same silhouette as SectionNav.
  return (
    <div
      ref={rowRef}
      onWheel={handleWheel}
      className="section-nav-scroll flex shrink-0 items-center gap-2 overflow-x-auto border-b border-border/60 px-3 lg:px-4"
    >
      <span className="sr-only">{label}</span>
      <div className="flex min-w-0 items-center py-1.5">{switcher}</div>
      {children && <div className="ml-auto flex shrink-0 items-center py-1.5 pl-3">{children}</div>}
    </div>
  )
}

// Generalized tab switcher for a pane header (the studio's People|Plan tabs).
// `options`: [{ value, label, icon, controls? }].
export function ModeTabs({ value, onChange, options, ariaLabel = 'Pane section' }) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex min-w-0 items-center gap-1"
    >
      {options.map((option) => {
        const active = option.value === value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={option.controls}
            onClick={() => onChange(option.value)}
            className={[
              'relative inline-flex shrink-0 items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none',
              active
                ? 'text-foreground after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:bg-primary'
                : 'text-muted-foreground',
            ].join(' ')}
          >
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
            <span>{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
